/**
 * Installability links for THIS tenant app.
 *
 * BOE-51 — the manifest is a REAL, same-origin file now. One build of this
 * shell is served from ONE bucket to every tenant distribution, so it cannot
 * ship a per-tenant `dist/manifest.webmanifest`. The backend instead writes each
 * tenant's manifest and pre-rendered icons into that same bucket under a
 * hostname-keyed prefix (metaship-backend Services/TenantApps/pwa-publish.ts):
 *
 *   https://<host>/pwa/<host>/manifest.webmanifest
 *   https://<host>/pwa/<host>/apple-touch-icon.png
 *
 * Every tenant distribution serves the whole bucket, so those URLs are on the
 * page's own origin. The shell only has to POINT at them, which it can do
 * before the tenant resolves — the hostname is all it needs.
 *
 * It used to compose the manifest in the browser and inject it as a `blob:`
 * URL with canvas `data:` icons. Chrome on Android does not install from a blob
 * manifest, so no tenant app ever offered to install on a phone — and an
 * installed app could never pick up a change to its manifest (BOE-59's bars
 * needed a reinstall). A real file at a stable URL is re-read by Android.
 *
 * One manifest per app, `standalone` everywhere (BOE-59): the phone keeps its
 * own bars in the app's colour. There is no phone variant.
 */

/** The prefix pwa-publish.ts writes under — keep the two in step. */
export const PWA_PATH_ROOT = "/pwa";

/** Same-origin path of one of this host's PWA files. */
export function pwaAssetPath(hostname: string, file: string): string {
  return `${PWA_PATH_ROOT}/${hostname.trim().toLowerCase()}/${file}`;
}

export function manifestPath(hostname: string): string {
  return pwaAssetPath(hostname, "manifest.webmanifest");
}

function upsertLink(doc: Document, rel: string, href: string): void {
  let el = doc.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = doc.createElement("link");
    el.rel = rel;
    doc.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertMeta(doc: Document, name: string, content: string): void {
  let el = doc.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = doc.createElement("meta");
    el.setAttribute("name", name);
    doc.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Point `<link rel="manifest">` and `<link rel="apple-touch-icon">` at this
 * host's published files. Called first thing at boot, synchronously, so the
 * browser can evaluate installability while the tenant is still resolving.
 * Uses the REAL hostname (not a dev `tenantHost` override): the files only
 * exist on the tenant's own distribution.
 */
export function linkPwaAssets(hostname: string, doc: Document = document): void {
  if (!hostname) return;
  upsertLink(doc, "manifest", manifestPath(hostname));
  upsertLink(doc, "apple-touch-icon", pwaAssetPath(hostname, "apple-touch-icon.png"));
}

/**
 * iOS reads the home-screen title from `apple-mobile-web-app-title`, not the
 * manifest — set it to the manifest's `short_name` (the tenant's own name).
 */
export function applyPwaTitle(name: string, doc: Document = document): void {
  const title = name.trim();
  if (!title) return;
  upsertMeta(doc, "apple-mobile-web-app-title", title);
  upsertMeta(doc, "application-name", title);
}
